/**
 * ADServiceDcManager — ¿Qué Domain Controllers pertenecen a ESTE servicio AD?
 *
 * Antes la topología AD mostraba siempre todos los DCs de la compañía, así que
 * dos servicios AD (multisede + una sola sede) salían idénticos. Aquí se
 * asignan explícitamente los CIs (servidores o VMs) que forman este servicio.
 *
 * - Sin asignar ninguno → modo automático (todos los DCs de la compañía).
 * - Asignado pero sin marcar como DC en su ficha → se avisa y se enlaza.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, TrashIcon, MagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const FSMO_SHORT = {
  schema_master: 'Schema',
  domain_naming_master: 'Naming',
  pdc_emulator: 'PDC',
  rid_master: 'RID',
  infrastructure_master: 'Infra',
};

function Chip({ children, tone = 'neutral', title }) {
  const tones = {
    neutral: { bg: 'var(--bg-input)', fg: 'var(--text-muted)' },
    blue: { bg: 'rgba(59,130,246,.15)', fg: '#93c5fd' },
    green: { bg: 'rgba(16,185,129,.18)', fg: '#6ee7b7' },
    amber: { bg: 'rgba(245,158,11,.18)', fg: '#fbbf24' },
    red: { bg: 'rgba(239,68,68,.15)', fg: '#fca5a5' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      title={title}
      style={{
        background: t.bg, color: t.fg, fontSize: '0.66rem', fontWeight: 700,
        borderRadius: 4, padding: '1px 6px', letterSpacing: '.03em',
        textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export default function ADServiceDcManager({
  serviceId, companyId, dcIds, onChange, sites = [],
}) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [onlyDc, setOnlyDc]         = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getADTopologyDcCandidates(serviceId, companyId)
      .then(res => { if (alive) setCandidates(res.candidates || []); })
      .catch(e => { if (alive) setError(e.message || 'No se pudieron cargar los candidatos'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [serviceId, companyId]);

  const byKey = useMemo(() => {
    const map = {};
    candidates.forEach(c => { map[`${c.kind}:${c.id}`] = c; });
    return map;
  }, [candidates]);

  const assigned = useMemo(
    () => (dcIds || []).map(ref => ({ ref, info: byKey[`${ref.kind}:${ref.id}`] })),
    [dcIds, byKey]
  );

  const assignedKeys = useMemo(
    () => new Set((dcIds || []).map(r => `${r.kind}:${r.id}`)),
    [dcIds]
  );

  const siteNameFor = (ref) => {
    const info = byKey[`${ref.kind}:${ref.id}`];
    if (!info) return '';
    if (info.site) return info.site;
    if (info.location_name) return info.location_name;
    return '';
  };

  const addable = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates
      .filter(c => !assignedKeys.has(`${c.kind}:${c.id}`))
      .filter(c => (onlyDc ? c.ad_dc : true))
      .filter(c => !q || (c.hostname || c.name || '').toLowerCase().includes(q)
        || (c.location_name || '').toLowerCase().includes(q))
      .slice(0, 40);
  }, [candidates, assignedKeys, search, onlyDc]);

  const notDeclared = assigned.filter(a => a.info && !a.info.ad_dc);

  const add = (c) => onChange([...(dcIds || []), { kind: c.kind, id: c.id }]);
  const remove = (ref) => onChange(
    (dcIds || []).filter(r => !(r.kind === ref.kind && r.id === ref.id))
  );

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--slate-300)',
      borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Domain Controllers de este servicio
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {assigned.length}
          </span>
        </div>
        {(dcIds || []).length > 0 && (
          <button className="dv-btn" type="button" onClick={() => onChange([])}
                  title="Volver al modo automático: se mostrarán todos los DC de la compañía">
            Quitar todas las asignaciones
          </button>
        )}
      </div>

      {(dcIds || []).length === 0 ? (
        <div style={{
          fontSize: '0.78rem', color: '#fbbf24',
          background: 'rgba(245,158,11,.08)', border: '1px dashed rgba(245,158,11,.4)',
          borderRadius: 6, padding: '8px 10px',
        }}>
          Modo automático: esta vista muestra <strong>todos</strong> los DC de la compañía.
          Si tienes más de un servicio AD (p. ej. uno multisede y otro de una sola sede),
          asigna aquí los DCs de cada uno para separarlos.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {assigned.map(({ ref, info }) => (
            <div
              key={`${ref.kind}:${ref.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                border: '1px solid var(--slate-200)', borderRadius: 6, padding: '6px 10px',
              }}
            >
              <Chip tone={ref.kind === 'vm' ? 'blue' : 'neutral'}>{ref.kind === 'vm' ? 'VM' : 'Servidor'}</Chip>
              <strong style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                {info ? (info.hostname || info.name) : `${ref.kind} #${ref.id} (no encontrado)`}
              </strong>
              {info?.is_global_catalog && <Chip tone="green" title="Global Catalog">GC</Chip>}
              {(info?.fsmo_roles || []).map(role => (
                <Chip key={role} tone={role === 'pdc_emulator' ? 'amber' : 'neutral'} title={role}>
                  {FSMO_SHORT[role] || role}
                </Chip>
              ))}
              {(info?.site || siteNameFor(ref)) && (
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {info?.site || siteNameFor(ref)}
                </span>
              )}
              <div style={{ flex: 1 }} />
              <button className="dv-btn" type="button" onClick={() => remove(ref)} title="Quitar de este servicio">
                <TrashIcon style={{ width: 14, height: 14 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {notDeclared.length > 0 && (
        <div style={{
          marginTop: 8, fontSize: '0.76rem', color: '#fca5a5',
          background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.35)',
          borderRadius: 6, padding: '8px 10px',
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <ExclamationTriangleIcon style={{ width: 15, height: 15, flexShrink: 0, marginTop: 2 }} />
          <div>
            {notDeclared.length === 1 ? 'Hay un CI asignado' : `Hay ${notDeclared.length} CIs asignados`} a este
            servicio que <strong>no están marcados como Domain Controller</strong> en su ficha
            ({notDeclared.map(a => (a.info ? (a.info.hostname || a.info.name) : a.ref.id)).join(', ')}).
            Ábrelos y activa <em>«es Domain Controller»</em> (servidores: Organización → Active Directory;
            VMs: pestaña Active Directory) para que aporten su dominio, sitio, GC y roles FSMO.
          </div>
        </div>
      )}

      {/* ── Añadir DCs ── */}
      <div style={{ marginTop: 12, borderTop: '1px solid var(--slate-200)', paddingTop: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <label style={{ fontSize: '0.74rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <MagnifyingGlassIcon style={{ width: 14, height: 14 }} />
            <input
              className="form-control form-control-sm"
              style={{ width: 220 }}
              placeholder="Buscar servidor o VM…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </label>
          <label style={{ fontSize: '0.74rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={onlyDc} onChange={e => setOnlyDc(e.target.checked)} />
            Sólo CIs marcados como DC
          </label>
          {loading && <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>Cargando catálogo…</span>}
          {error && <span style={{ fontSize: '0.74rem', color: '#f87171' }}>{error}</span>}
        </div>

        {addable.length === 0 ? (
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            {onlyDc
              ? 'No hay más CIs marcados como DC. Desmarca el filtro para asignar uno que aún no lo esté.'
              : 'Sin resultados.'}
          </div>
        ) : (
          <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {addable.map(c => (
              <div key={`${c.kind}:${c.id}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 8px', borderRadius: 6, background: 'var(--bg-input)',
              }}>
                <Chip tone={c.kind === 'vm' ? 'blue' : 'neutral'}>{c.kind === 'vm' ? 'VM' : 'SRV'}</Chip>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>{c.hostname || c.name}</span>
                {c.is_global_catalog && <Chip tone="green">GC</Chip>}
                {(c.fsmo_roles || []).length > 0 && (
                  <Chip tone="amber">{c.fsmo_roles.map(r => FSMO_SHORT[r] || r).join(' · ')}</Chip>
                )}
                {c.location_name && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>📍 {c.location_name}</span>
                )}
                {!c.ad_dc && <Chip tone="red" title="No está marcado como DC en su ficha">no es DC</Chip>}
                <div style={{ flex: 1 }} />
                <button className="dv-btn" type="button" onClick={() => add(c)} title="Añadir a este servicio">
                  <PlusIcon style={{ width: 14, height: 14 }} /> Añadir
                </button>
              </div>
            ))}
          </div>
        )}
        {sites.length > 0 && (
          <div style={{ marginTop: 8, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            La sede de cada DC se deduce de la ubicación de su rack (o del campo «Sitio AD»).
            Ajusta los nombres y subredes en el bloque <strong>Sedes documentadas</strong>.
          </div>
        )}
      </div>
    </div>
  );
}
